/* eslint-disable react-hooks/exhaustive-deps */
import { ChangeEventHandler } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Box,
} from "@material-ui/core";
import {
  AdvancedOptionsContainer,
  Label,
  OptionGroup,
} from "components/Styled";
import SwitchField from "components/Forms/SwitchField";
import { Field, FormFields } from "pages/Rules/common/FormFields";
import { isValidOptionForZipPassword } from "pages/Rules/Form/context/reducers/validateStep/action";
import { RuleType, RuleUrlParams } from "pages/Rules/common/types";
import { isBlank } from "utils/string";
import PasswordNotificationSettings, {
  isAllowPasswordNotification,
} from "./PasswordNotificationSettings";
import StorageNotificationTemplateSelect from "./StorageNotificationTemplateSelect";
import { ErrorMessage, StyledDetails, StyledSummary } from "./Styled";
import { isAttachment, mapLinkFormat } from "./utils";
import { Alert } from "@material-ui/lab";
import { ZipAttachments } from "@dashboard-v3/api";
import { NOTIFICATION_LIMITS } from "@dashboard-v3/shared";
import { ActionState } from "./types";

interface OptionsProps {
  values: ActionState;
  setValues: (updates: Partial<ActionState>) => void;
  ruleType: RuleType;
  targetType?: string;
  disabled: boolean;
}

export default function AdvancedOptions({
  ruleType,
  values,
  setValues,
  targetType,
  disabled,
}: OptionsProps) {
  const { t } = useTranslation("rules");

  const { action }: RuleUrlParams = useParams();

  const disableAdvancedOptions = action === "edit";

  const handleLinkFormat = event => {
    const { value } = event.target;
    let linksFormat = [value];

    if (value === "BOTH") {
      linksFormat = ["PDF", "LINK"];
    }

    setValues({ linksFormat });
  };

  const handlePasswordNotification = event => {
    const { name, checked } = event.target;
    setValues({ [name]: checked });
  };

  function toggleSenderNotification(event) {
    const { checked } = event.target;

    if (checked) {
      setValues({
        senderNotification: {
          title: "Email processed with ZIP encryption",
          bodyText:
            "Your email was processed to secure its attachments using password-protected ZIP format. The recipients have received a secure version.",
        },
      });
      return;
    }

    setValues({
      senderNotification: null,
    });
  }

  function handleSenderNotificationChange(changes) {
    setValues({
      senderNotification: { ...changes },
    });
  }

  function isValidPasswordNotification() {
    return isValidOptionForZipPassword(
      values.passwordNotificationToRecipient,
      values.passwordNotificationToSender
    );
  }

  const handleTrash = event => {
    const { value } = event.target;
    setValues({ trash: value === "true" });
  };

  const handleMoveOriginalToTrashOnReplace = event => {
    const { value } = event.target;
    setValues({ moveOriginalToTrashOnReplace: value === "true" });
  };

  const onNotificationChange: ChangeEventHandler<HTMLInputElement> = e => {
    const { name, value } = e.target;
    const { blockNofication } = values;

    setValues({
      blockNofication: { ...blockNofication, [name]: value },
    });
  };

  if (disabled) return <></>;

  if (ruleType === "BLOCK_ATTACHMENTS") {
    return (
      <AdvancedOptionsContainer>
        <StyledSummary>
          <Typography>{t("advancedOptions")}</Typography>
        </StyledSummary>
        <StyledDetails>
          <OptionGroup>
            <Label
              wording={t("blockNotifications.sectionTitle")}
              gutterBottom
            />
            <TextField
              name="title"
              label={t("blockNotifications.field.titleLabel")}
              variant="outlined"
              value={values.blockNofication.title}
              fullWidth
              style={{ marginTop: 10 }}
              onChange={onNotificationChange}
              inputProps={{ maxLength: NOTIFICATION_LIMITS.TITLE }}
            />
            <TextField
              name="bodyText"
              multiline
              label={t("blockNotifications.field.bodyTextLabel")}
              variant="outlined"
              value={values.blockNofication.bodyText}
              fullWidth
              style={{ marginTop: 20 }}
              onChange={onNotificationChange}
              inputProps={{ maxLength: NOTIFICATION_LIMITS.BODY_TEXT }}
            />
          </OptionGroup>
        </StyledDetails>
      </AdvancedOptionsContainer>
    );
  }

  return (
    <AdvancedOptionsContainer>
      <StyledSummary>
        <Typography>{t("advancedOptions")}</Typography>
      </StyledSummary>
      <StyledDetails>
        {isAttachment(ruleType) && (
          <OptionGroup>
            <Label wording={t("selectWhereToAddTheAttachmentLinks")} />
            <RadioGroup
              name="linkFormat"
              value={mapLinkFormat(values.linksFormat)}
              onChange={handleLinkFormat}
            >
              <FormControlLabel
                control={<Radio color="primary" size="small" />}
                label={t("emailsBody")}
                value="LINK"
              />
              <FormControlLabel
                control={<Radio color="primary" size="small" />}
                label={t("attachmentPdf")}
                value="PDF"
              />
              <FormControlLabel
                control={<Radio color="primary" size="small" />}
                label={t("common:both")}
                value="BOTH"
              />
            </RadioGroup>
          </OptionGroup>
        )}

        {ruleType === "MOVE_EMAILS" && (
          <OptionGroup>
            <Label wording={t("ruleAction.advanced.moveEmails.title")} />
            <RadioGroup
              name="trash"
              value={values.trash?.toString() || "true"}
              onChange={handleTrash}
              aria-disabled={disableAdvancedOptions}
            >
              <FormControlLabel
                control={<Radio color="primary" size="small" />}
                label={t("ruleAction.advanced.moveEmails.trash")}
                value="true"
                disabled={disableAdvancedOptions}
              />
              <FormControlLabel
                control={<Radio color="primary" size="small" />}
                label={t("ruleAction.advanced.moveEmails.delete")}
                value="false"
                disabled={disableAdvancedOptions}
              />
            </RadioGroup>
            {values.trash === false && (
              <Alert severity="warning" style={{ marginTop: 8 }}>
                {t("ruleAction.advanced.deleteWarning")}
              </Alert>
            )}
          </OptionGroup>
        )}

        {ruleType === "REPLACE_ATTACHMENTS" && targetType === "MAIL_BOX" && (
          <OptionGroup>
            <Label
              wording={t("ruleAction.advanced.replaceAttachments.title")}
            />
            <RadioGroup
              name="moveOriginalToTrashOnReplace"
              value={values.moveOriginalToTrashOnReplace?.toString() ?? "false"}
              onChange={handleMoveOriginalToTrashOnReplace}
              aria-disabled={disableAdvancedOptions}
            >
              <FormControlLabel
                control={<Radio color="primary" size="small" />}
                label={t("ruleAction.advanced.replaceAttachments.trash")}
                value="true"
                disabled={disableAdvancedOptions}
              />
              <FormControlLabel
                control={<Radio color="primary" size="small" />}
                label={t("ruleAction.advanced.replaceAttachments.delete")}
                value="false"
                disabled={disableAdvancedOptions}
              />
            </RadioGroup>
            {values.moveOriginalToTrashOnReplace === false && (
              <Alert severity="warning" style={{ marginTop: 8 }}>
                {t("ruleAction.advanced.deleteWarning")}
              </Alert>
            )}
          </OptionGroup>
        )}

        <StorageNotificationTemplateSelect />

        {ruleType === "ZIP_ATTACHMENTS" && (
          <>
            <OptionGroup>
              <SwitchField
                name="passwordNotificationToRecipient"
                label={t("zipPassword.sendToRecipient.label")}
                description={t("zipPassword.sendToRecipient.desc")}
                value={values.passwordNotificationToRecipient}
                onChange={handlePasswordNotification}
              />
              <ErrorMessage
                isError={!isValidPasswordNotification()}
                wording={t("zipPassword.notificationError")}
              />
            </OptionGroup>

            <OptionGroup>
              <SwitchField
                name="passwordNotificationToSender"
                label={t("zipPassword.sendToSender.label")}
                description={t("zipPassword.sendToSender.desc")}
                value={values.passwordNotificationToSender}
                onChange={handlePasswordNotification}
              />
            </OptionGroup>

            <OptionGroup>
              <SwitchField
                name="senderNotification"
                label="Send a notification to the sender"
                description="The sender will be notified that the email sent to a recipient for whom ZIP encryption is mandatory has been subjected to the ZIP encryption rules."
                value={Boolean(values.senderNotification)}
                onChange={toggleSenderNotification}
              />

              {values.senderNotification && (
                <Box mt="1.5rem" ml="1rem">
                  <SenderNotification
                    value={values.senderNotification}
                    onChange={handleSenderNotificationChange}
                  />
                </Box>
              )}
            </OptionGroup>
          </>
        )}
        {isAllowPasswordNotification(ruleType) && (
          <PasswordNotificationSettings values={values} setValues={setValues} />
        )}
      </StyledDetails>
    </AdvancedOptionsContainer>
  );
}

function SenderNotification({
  value,
  onChange,
}: {
  value: ZipAttachments["senderNotification"];
  onChange: (change: Record<string, string>) => void;
}) {
  const { t } = useTranslation("rules");

  function handleChange(event) {
    const { name, value } = event.target;
    onChange({ [name]: value });
  }

  return (
    <FormFields>
      <Field>
        <TextField
          name="title"
          variant="outlined"
          label={t("senderNotification.title.label")}
          value={value.title}
          onChange={handleChange}
          error={isBlank(value.title)}
          inputProps={{ maxLength: NOTIFICATION_LIMITS.TITLE }}
        />
        <ErrorMessage
          isError={isBlank(value.title)}
          wording="Cannot be blank"
        />
      </Field>
      <Field>
        <TextField
          name="bodyText"
          variant="outlined"
          label={t("senderNotification.bodyText.label")}
          value={value.bodyText}
          onChange={handleChange}
          error={isBlank(value.bodyText)}
          inputProps={{ maxLength: NOTIFICATION_LIMITS.BODY_TEXT }}
        />
        <ErrorMessage
          isError={isBlank(value.bodyText)}
          wording="Cannot be blank"
        />
      </Field>
    </FormFields>
  );
}
